import type React from 'react';

import validate from '@vlabs/shared/validators';
import { Button } from '@vlabs/uikit/components/controls/button';
import { Input } from '@vlabs/uikit/components/controls/input';
import { Textarea } from '@vlabs/uikit/components/controls/textarea/Textarea';
import { useForm } from 'react-hook-form';

import type { CreateDepartmentRequest } from '@vlabs/api/artemy/model';

import st from './CreateDepartmentForm.module.sass';
import { useCreateDepartmentTranslation } from './i18n';

type CreateDepartmentFormValues = Pick<CreateDepartmentRequest, 'name' | 'description'>;

interface CreateDepartmentFormProps {
  onSubmit: (values: CreateDepartmentFormValues) => void | Promise<void>;
  onCancel?: VoidFunction;
}

function CreateDepartmentForm({
  onSubmit,
  onCancel = undefined,
}: CreateDepartmentFormProps): React.JSX.Element {
  const { t } = useCreateDepartmentTranslation();
  const form = useForm<CreateDepartmentFormValues>();

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div
        className={st.CreateDepartmentForm}
        data-testid="CreateDepartmentForm"
      >
        <h5>{t('title')}</h5>

        <div className={st.Fields}>
          <div className={st.Field}>
            <Input
              id="name"
              label={t('fields.name')}
              {...form.register('name', {
                required: validate.required(),
                minLength: validate.minLength(3),
                maxLength: validate.maxLength(128),
              })}
              errors={form.formState.errors}
            />
          </div>

          <div className={st.Field}>
            <Textarea
              id="description"
              label={t('fields.description')}
              {...form.register('description', {
                maxLength: validate.maxLength(512),
              })}
              errors={form.formState.errors}
            />
          </div>
        </div>

        <div className={st.Actions}>
          <Button
            className={st.Button}
            type="submit"
          >
            {t('submit')}
          </Button>

          <Button
            className={st.Button}
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}

CreateDepartmentForm.displayName = 'CreateDepartmentForm';

export { CreateDepartmentForm };
export type { CreateDepartmentFormValues };
